import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { aiApi } from '../api';

// Feed de decisões ao vivo (Fase 1 do teardown 2026-10) — inspirado no feed
// público do Alpha Arena: cada tick de cada cérebro vira uma entrada com o que
// ele VIU (sinal MTF + leitura por timeframe) e o que FEZ (ação + motivo).
// O dado vem de brain_decisions no Postgres (já gravado tick a tick).

const ACTION_LABEL = {
  open: { label: 'ABRIU', color: '#34d399', bg: 'rgba(52,211,153,0.08)' },
  close: { label: 'FECHOU', color: '#f87171', bg: 'rgba(248,113,113,0.08)' },
  hold: { label: 'segurando', color: '#94a3b8', bg: 'transparent' },
  cooldown: { label: 'cooldown pós-stop', color: '#fbbf24', bg: 'rgba(251,191,36,0.06)' },
  no_entry_confidence: { label: 'sinal fraco', color: '#64748b', bg: 'transparent' },
  no_entry_conviction: { label: 'sem convicção', color: '#64748b', bg: 'transparent' },
  m1_against: { label: '1m contra', color: '#64748b', bg: 'transparent' },
  sentiment_bar: { label: 'sentimento contra', color: '#64748b', bg: 'transparent' },
  odds_block: { label: 'odds contra', color: '#f87171', bg: 'rgba(248,113,113,0.06)' },
  open_rejected: { label: 'entrada recusada', color: '#fbbf24', bg: 'rgba(251,191,36,0.06)' },
};

const BRAIN_META = {
  brain_conservative: { name: 'Conservador', emoji: '🛡️', color: '#38bdf8' },
  brain_moderate: { name: 'Moderado', emoji: '⚖️', color: '#a78bfa' },
  brain_aggressive: { name: 'Agressivo', emoji: '🔥', color: '#fb923c' },
};

function timeAgo(iso) {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function SigTag({ signal, confidence }) {
  if (!signal) return null;
  const color = signal === 'buy' ? '#34d399' : signal === 'sell' ? '#f87171' : '#94a3b8';
  return (
    <span style={{ color, fontWeight: 700 }}>
      {signal.toUpperCase()}{confidence != null && confidence > 0 ? ` ${Math.round(confidence * 100)}%` : ''}
    </span>
  );
}

export default function DecisionFeed({ symbols }) {
  const [expanded, setExpanded] = useState(null);
  const [onlyActions, setOnlyActions] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ['brain-decisions', onlyActions],
    queryFn: () => aiApi.get(`/brains/decisions?limit=150`).then(r => r.data),
    refetchInterval: 15_000,
  });

  let decisions = data?.decisions || [];
  if (onlyActions) {
    // Ações relevantes primeiro: abriu/fechou/recusada + cooldown (o "hold"
    // puro é ruído — o cérebro segurando não é notícia)
    decisions = decisions.filter(d => !['hold'].includes(d.action));
  }
  const shown = decisions.slice(0, 40);

  return (
    <div style={{
      background: '#0b1220', border: '1px solid #1e293b', borderRadius: 12,
      padding: '14px 18px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: '#e2e8f0' }}>
          🧠 Raciocínio ao vivo
        </span>
        <span style={{ fontSize: 11, color: '#475569' }}>
          o que cada cérebro viu e decidiu, tick a tick (auditável — igual arena pública)
        </span>
        <button
          onClick={() => setOnlyActions(v => !v)}
          style={{
            marginLeft: 'auto', background: '#0f172a', color: onlyActions ? '#38bdf8' : '#64748b',
            border: '1px solid #334155', borderRadius: 8, padding: '4px 12px',
            fontSize: 11, cursor: 'pointer',
          }}
        >
          {onlyActions ? 'só ações' : 'tudo (inclui holds)'}
        </button>
      </div>

      {isLoading ? (
        <div style={{ fontSize: 12, color: '#475569' }}>carregando decisões…</div>
      ) : shown.length === 0 ? (
        <div style={{ fontSize: 12, color: '#475569' }}>
          sem decisões ainda — aguarde o próximo tick (1/min) ou ligue os cérebros.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {shown.map(d => {
            const meta = BRAIN_META[d.brain_id] || { name: d.brain_id, emoji: '🧠', color: '#94a3b8' };
            const act = ACTION_LABEL[d.action] || { label: d.action, color: '#94a3b8', bg: 'transparent' };
            const isBig = d.action === 'open' || d.action === 'close';
            const isOpen = expanded === d.id;
            let tfMap = {};
            try { tfMap = typeof d.tf_signals === 'string' ? JSON.parse(d.tf_signals) : (d.tf_signals || {}); } catch { /* ignore */ }
            return (
              <div
                key={d.id}
                onClick={() => setExpanded(isOpen ? null : d.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  padding: isBig ? '8px 10px' : '5px 10px', borderRadius: 8,
                  background: act.bg, cursor: 'pointer',
                  border: isBig ? `1px solid ${act.color}33` : '1px solid transparent',
                }}
              >
                <span style={{ fontSize: 10, color: '#475569', width: 44 }}>{timeAgo(d.ts)}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: meta.color, width: 92 }}>
                  {meta.emoji} {meta.name}
                </span>
                <span style={{ fontSize: 11, color: '#64748b', width: 56 }}>
                  {d.symbol?.replace('USDT', '')}
                </span>
                <span style={{ fontSize: 11, color: '#64748b' }}>
                  viu <SigTag signal={d.mtf_signal} confidence={d.mtf_confidence} />
                </span>
                <span style={{
                  fontSize: isBig ? 12 : 11, fontWeight: isBig ? 800 : 500,
                  color: act.color,
                }}>
                  → {act.label}
                  {d.action === 'open' && d.action_signal && (
                    <span style={{ color: d.action_signal === 'buy' ? '#34d399' : '#f87171' }}>
                      {' '}{d.action_signal.toUpperCase()} {d.source && d.source !== 'MTF' ? `(via ${d.source})` : ''}
                    </span>
                  )}
                  {d.action === 'close' && d.position_pnl_pct != null && (
                    <span style={{ color: d.position_pnl_pct >= 0 ? '#34d399' : '#f87171' }}>
                      {' '}{d.position_side} {d.position_pnl_pct >= 0 ? '+' : ''}{d.position_pnl_pct?.toFixed(1)}%
                    </span>
                  )}
                </span>
                {d.price && (
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#475569' }}>
                    ${d.price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </span>
                )}

                {isOpen && (
                  <div style={{
                    width: '100%', marginTop: 6, padding: '8px 10px',
                    background: '#0f172a', borderRadius: 8, fontSize: 11,
                    display: 'flex', gap: 8, flexWrap: 'wrap',
                  }}>
                    <span style={{ color: '#64748b' }}>leitura por timeframe:</span>
                    {Object.entries(tfMap).map(([tf, s]) => (
                      <span key={tf} style={{
                        background: '#1e293b', borderRadius: 6, padding: '2px 8px',
                        border: '1px solid #334155',
                      }}>
                        <b style={{ color: '#94a3b8' }}>{tf}</b>{' '}
                        <SigTag signal={s.signal} confidence={s.confidence} />
                      </span>
                    ))}
                    {d.conviction != null && (
                      <span style={{ color: '#64748b' }}>
                        · convicção: <b style={{ color: '#e2e8f0' }}>{(d.conviction * 100).toFixed(0)}%</b>
                      </span>
                    )}
                    {d.source && (
                      <span style={{ color: '#64748b' }}>· origem: <b style={{ color: '#e2e8f0' }}>{d.source}</b></span>
                    )}
                    {d.odds_verdict && d.odds_verdict !== 'no_data' && d.odds_tp_first != null && (
                      <span style={{ color: '#64748b' }}>
                        · odds: <b style={{ color: d.odds_tp_first >= d.odds_sl_first ? '#34d399' : '#f87171' }}>
                          TP {Number(d.odds_tp_first).toFixed(1)}% × SL {Number(d.odds_sl_first).toFixed(1)}%
                        </b>
                        <span style={{ color: '#475569' }}> ({d.odds_verdict})</span>
                      </span>
                    )}
                    {d.odds_reason === 'error' && (
                      <span style={{ color: '#475569' }}>· odds indisponíveis neste tick</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
